import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useGlobal } from "../../contexts/globalContext";
import RegisterModal from "../RegisterModal";
import Room from "../Room";
import * as S from "./styles";

const MultipleRoomSelector = ({ searchState, hotel, nights, buscaAtual, boardTypeFilter = "all" }) => {
  const { user, signed } = useGlobal();
  const history = useHistory();
  const [selectedRooms, setSelectedRooms] = useState({});
  const [isOpenRegisterModal, setIsOpenRegisterModal] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const selectedRoomsSectionRef = useRef(null);

  const getValuesByUser = (val) => {
    let value = signed
      ? val * user.catalog.catalogCurrencyConfig.conversionRate
      : val;

    return signed ? Math.ceil(Math.round(value)) : value;
  };

  const getAllAvailableRooms = () => {
    const allRooms = [];

    if (hotel?.roomStays.roomTypes) {
      hotel.roomStays.roomTypes.forEach((room) => {
        room.roomRates.forEach((rate) => {
          allRooms.push({
            ...room,
            selectedRate: rate,
          });
        });
      });
    }

    const filtered = boardTypeFilter === "all"
      ? allRooms
      : allRooms.filter((room) => {
          const mealPlan = room.selectedRate?.mealPlan || "";
          return mealPlan === boardTypeFilter;
        });

    filtered.sort((a, b) => {
      const priceA =
        a.selectedRate?.[
          a.selectedRate.total.currencyCode === "BRL"
            ? "total"
            : "origTotal" || "total"
        ].amountAfterTax || 0;
      const priceB =
        b.selectedRate?.[
          b.selectedRate.total.currencyCode === "BRL"
            ? "total"
            : "origTotal" || "total"
        ].amountAfterTax || 0;
      return priceA - priceB;
    });

    return filtered;
  };

  const availableRooms = getAllAvailableRooms();

  const handleRoomSelection = (rph, room, rate) => {
    setSelectedRooms((prev) => ({
      ...prev,
      [rph]: {
        room,
        rate,
        roomName: room.roomName,
        nights,
        totalTax:
          rate?.[
            rate.total.currencyCode === "BRL" ? "total" : "origTotal" || "total"
          ].amountAfterTax,
        hotel,
        searchState,
        rateSelected: rate,
        rph,
        // Orinter-specific fields for checkRate/booking
        refToken: room?.refToken || rate?.refToken,
        key: room?.key || rate?.key,
      },
    }));

    // Auto-advance to next tab after selection
    setTimeout(() => {
      const nextTab = rph + 1;
      if (nextTab < searchState.qtRooms) {
        setCurrentTab(nextTab);
      }
    }, 500); // Small delay for better UX
  };

  const handleRemoveRoomSelection = (rph) => {
    setSelectedRooms((prev) => {
      const updated = { ...prev };
      delete updated[rph];

      let firstEmptySlot = 0;
      for (let i = 0; i < searchState.qtRooms; i++) {
        if (!updated[i]) {
          firstEmptySlot = i;
          break;
        }
      }

      setCurrentTab(firstEmptySlot);

      return updated;
    });
  };

  const handleRegisterModal = () => setIsOpenRegisterModal((prev) => !prev);

  const handleProceedToSummary = () => {
    const selectedRoomsCount = Object.keys(selectedRooms).length;

    if (selectedRoomsCount !== searchState.qtRooms) {
      alert(
        `Por favor, selecione exatamente ${searchState.qtRooms} quarto(s). Você selecionou ${selectedRoomsCount}.`,
      );
      return;
    }

    for (let i = 0; i < searchState.qtRooms; i++) {
      if (!selectedRooms[i]) {
        alert(`Por favor, selecione um quarto para o Quarto ${i + 1}.`);
        return;
      }
    }

    const roomsArray = [];
    for (let i = 0; i < searchState.qtRooms; i++) {
      if (selectedRooms[i]) {
        roomsArray.push(selectedRooms[i]);
      }
    }

    sessionStorage.setItem("@roomSelected", JSON.stringify(roomsArray));

    if (user?.guest || user?.travelVoucher) {
      return handleRegisterModal();
    }
    history.push("/busca/resumo");
  };

  const canProceed = Object.keys(selectedRooms).length === searchState.qtRooms;

  useEffect(() => {
    if (
      Object.keys(selectedRooms).length > 0 &&
      selectedRoomsSectionRef.current
    ) {
      selectedRoomsSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [Object.keys(selectedRooms).length]);

  return (
    <S.Wrapper>
      <RegisterModal
        open={isOpenRegisterModal}
        onClose={handleRegisterModal}
        travelVoucherUser={user?.travelVoucher}
      />

      {/* Selected Rooms Summary */}
      {Object.keys(selectedRooms).length > 0 && (
        <S.SelectedRoomsSection ref={selectedRoomsSectionRef}>
          <S.SelectedRoomsHeader>
            <S.SectionTitle>Quartos Selecionados:</S.SectionTitle>
            {canProceed && (
              <S.TotalPrice>
                Total:{" "}
                {signed &&
                !user?.guest &&
                !user?.campaign?.disablePointActivity ? (
                  <span>
                    {Object.values(selectedRooms).reduce(
                      (sum, room) => sum + getValuesByUser(room.totalTax),
                      0,
                    )}{" "}
                    {user?.campaign?.campaignConfig?.shortenCoinAlias || "Pts"}
                  </span>
                ) : (
                  <span>
                    R${" "}
                    {Object.values(selectedRooms)
                      .reduce(
                        (sum, room) => sum + getValuesByUser(room.totalTax),
                        0,
                      )
                      ?.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </span>
                )}
              </S.TotalPrice>
            )}
          </S.SelectedRoomsHeader>

          {Object.entries(selectedRooms).map(([rph, selection]) => (
            <S.SelectedRoomCard key={rph}>
              <S.SelectedRoomInfo>
                <S.RoomNumber>Quarto {parseInt(rph) + 1}</S.RoomNumber>
                <S.RoomName>{selection.roomName}</S.RoomName>
                <S.RoomPrice>
                  {signed &&
                  !user?.guest &&
                  !user?.campaign?.disablePointActivity ? (
                    <span>
                      {getValuesByUser(selection.totalTax)}{" "}
                      {user?.campaign?.campaignConfig?.shortenCoinAlias ||
                        "Pts"}
                    </span>
                  ) : (
                    <span>
                      R${" "}
                      {getValuesByUser(selection.totalTax)?.toLocaleString(
                        "pt-BR",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </span>
                  )}{" "}
                  total para {nights} noite(s)
                </S.RoomPrice>
                <S.RoomPriceDaily>
                  {signed &&
                  !user?.guest &&
                  !user?.campaign?.disablePointActivity ? (
                    <span>
                      {getValuesByUser(selection.totalTax / nights)}{" "}
                      {user?.campaign?.campaignConfig?.shortenCoinAlias ||
                        "Pts"}
                    </span>
                  ) : (
                    <span>
                      R${" "}
                      {getValuesByUser(
                        selection.totalTax / nights,
                      )?.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  )}{" "}
                  por noite
                </S.RoomPriceDaily>
              </S.SelectedRoomInfo>
              <S.RemoveButton onClick={() => handleRemoveRoomSelection(rph)}>
                Remover
              </S.RemoveButton>
            </S.SelectedRoomCard>
          ))}

          {/* Proceed Button - shown when all rooms selected */}
          {canProceed && (
            <S.ProceedSectionInline>
              <S.ProceedButton
                disabled={false}
                onClick={handleProceedToSummary}
                canProceed={true}
              >
                Prosseguir para Resumo
              </S.ProceedButton>
            </S.ProceedSectionInline>
          )}
        </S.SelectedRoomsSection>
      )}

      {/* Tabs for Each Room */}
      {!canProceed && (
        <S.TabsSection>
          <S.TabsTitle>Selecione os quartos:</S.TabsTitle>
          <S.TabsContainer>
            {Array.from({ length: searchState.qtRooms }, (_, index) => (
              <S.CustomTab
                key={index}
                isActive={currentTab === index}
                isCompleted={selectedRooms[index] ? true : false}
                onClick={() => setCurrentTab(index)}
              >
                <S.TabNumber
                  isActive={currentTab === index}
                  isCompleted={selectedRooms[index] ? true : false}
                >
                  {selectedRooms[index] ? "✓" : index + 1}
                </S.TabNumber>
                <S.TabLabel isActive={currentTab === index}>
                  Quarto {index + 1}
                </S.TabLabel>
                {selectedRooms[index] && (
                  <S.TabRoomName>{selectedRooms[index].roomName}</S.TabRoomName>
                )}
              </S.CustomTab>
            ))}
          </S.TabsContainer>
        </S.TabsSection>
      )}

      {/* Available Rooms for Current Tab */}
      {!canProceed && (
        <S.AvailableRoomsSection>
          <S.SectionTitle>
            {selectedRooms[currentTab] && (
              <>
                Quarto {currentTab + 1} selecionado:{" "}
                <S.SelectedIndicator>
                  {selectedRooms[currentTab].roomName}
                </S.SelectedIndicator>
                <br />
                Clique em outro quarto para alterar ou use as abas para navegar
              </>
            )}
          </S.SectionTitle>
          {availableRooms?.length > 0 ? (
            availableRooms.map((room, index) => (
              <S.RoomWrapper key={`${currentTab}-${index}`}>
                <Room
                  searchState={searchState}
                  roomName={room.roomName}
                  roomRates={[room.selectedRate]}
                  nights={nights}
                  hotel={hotel}
                  room={room}
                  buscaAtual={buscaAtual}
                  isMultipleSelection={true}
                  onRoomSelect={(selectedRoom, selectedRate) => {
                    handleRoomSelection(currentTab, selectedRoom, selectedRate);
                  }}
                  isSelected={selectedRooms[currentTab]?.room === room}
                />
              </S.RoomWrapper>
            ))
          ) : (
            <S.NoRoomsMessage>
              Nenhum quarto disponível para o Quarto {currentTab + 1}.
            </S.NoRoomsMessage>
          )}
        </S.AvailableRoomsSection>
      )}

      {/* All Rooms Selected Message */}
      {canProceed && (
        <S.AllRoomsSelectedSection>
          <S.CompletionIcon>✓</S.CompletionIcon>
          <S.CompletionTitle>
            Todos os quartos foram selecionados!
          </S.CompletionTitle>
          <S.CompletionSubtitle>
            Revise suas seleções acima ou prossiga para finalizar sua reserva.
          </S.CompletionSubtitle>
        </S.AllRoomsSelectedSection>
      )}

      {/* Proceed Button - shown when rooms incomplete */}
      {!canProceed && (
        <S.ProceedSection>
          <S.ProceedButton
            disabled={true}
            onClick={handleProceedToSummary}
            canProceed={false}
          >
            Selecione {searchState.qtRooms - Object.keys(selectedRooms).length}{" "}
            quarto(s) restante(s)
          </S.ProceedButton>
        </S.ProceedSection>
      )}
    </S.Wrapper>
  );
};

export default MultipleRoomSelector;
